/* /app/frontend/src/hooks/useDealTransactionState.js — iter-362
 *
 * The single source of truth for the five highest-risk Deal Room
 * panels:
 *
 *     • Transaction Journey  (which milestones are relevant + status)
 *     • Current Focus        (the first non-completed applicable step)
 *     • Waiting On           (the outstanding action, if any)
 *     • Health Status        (calm-vs-at-risk-vs-blocked)
 *     • Next Best Action     (the operator's next verb)
 *
 * Everything the panels read comes back from THIS hook. That
 * kills three long-standing bugs:
 *
 *   1. Contradictory states (Blocker outstanding + Health=Healthy)
 *   2. Missing-milestone drift (mortgage steps for cash buyers)
 *   3. Terminal-state noise (CTAs on Exchanged / Completed /
 *      Fallen Through deals)
 *
 * Design notes
 * ------------
 * * **Data-availability gates**. Skip rules only fire when the
 *   deal actually carries the flag. `is_cash_buyer` / `chain_free`
 *   are not present on every deal today — we gracefully fall back
 *   to the mortgage + chain path when the field is missing so
 *   the journey never regresses to a shorter incorrect version.
 * * **Terminal-first**. Terminal states short-circuit every panel
 *   so no CTA / waiting chip / focus label can leak through.
 * * **Pure & deterministic**. Same `deal` + `milestones` + `today`
 *   input → same output. Memoised at the callsite via `useMemo`.
 */
import { useMemo } from "react";

import { journeyFor, optsForDeal, isPreJourney, PRE_JOURNEY, closeCompletedSet, nodeIdForStage } from "../data/transactionJourney.js";
import {
  TERMINAL_STATUSES,
  TERMINAL_LABEL,
  daysSince,
  waitingChip,
  HEALTH_COPY,
} from "../copy/dealRoom.js";
import { computeExchangeConfidence } from "./exchangeConfidence.js";


/* ─── Field probes ────────────────────────────────────────────
 *
 * All accept either camelCase (`isCashBuyer`) or snake_case
 * (`is_cash_buyer`) since pilot deals come from the JS store
 * and real deals come from the Postgres API. Everything is
 * strictly optional — a missing flag means "unknown, don't skip".
 */

const _has = (deal, ...names) => {
  for (const n of names) {
    if (deal && Object.prototype.hasOwnProperty.call(deal, n) && deal[n] !== null && deal[n] !== undefined) {
      return true;
    }
  }
  return false;
};

const _bool = (deal, ...names) => {
  for (const n of names) {
    if (deal && Object.prototype.hasOwnProperty.call(deal, n) && deal[n] !== null && deal[n] !== undefined) {
      return !!deal[n];
    }
  }
  return null; // unknown
};


export function resolveDealFlags(deal) {
  const financeType = deal?.financeType || deal?.finance_type
    || (_bool(deal, "is_cash_buyer", "isCashBuyer") === true ? "cash" : null)
    || null;
  const chainFreeRaw = _bool(deal, "chain_free", "chainFree", "is_chain_free");
  return {
    tenure: (deal?.tenure || "leasehold").toLowerCase().includes("lease") ? "leasehold" : "freehold",
    financeType, // "cash" | "mortgage" | null (unknown)
    chainFree: chainFreeRaw, // true | false | null (unknown)
    // Only strip mortgage nodes when we KNOW the deal is cash.
    // Missing data ⇒ show the full path.
    skipMortgage: financeType === "cash",
    // Ditto for chain — only skip chain nodes when we KNOW the
    // deal is chain-free.
    skipChain:   chainFreeRaw === true,
    // Tenure skip is always safe because tenure is a hard field
    // on the Postgres deals table.
    skipManagementPack: (deal?.tenure || "").toLowerCase() !== "leasehold",
  };
}


/* Build the applicable journey nodes for THIS deal. Mirrors
 * `journeyFor(tenure, opts)` but layers in the chain-free skip
 * on top. `journeyFor` already handles tenure + mortgage. */
function _buildApplicableNodes(deal) {
  const flags = resolveDealFlags(deal);
  const opts = optsForDeal(deal || {});
  // Ensure `financeType` reflects our resolved value so
  // journeyFor's `requiresMortgage` gate picks up cash-buyer deals.
  if (flags.financeType) opts.financeType = flags.financeType;
  const base = journeyFor(flags.tenure, opts);
  return base.filter((node) => {
    if (flags.skipChain && node?.chainOnly) return false;
    return true;
  });
}


/* iter-alignment · P0 · NBA Completed-Stage Guard (2026-08-16)
 * — A canonical journey node (mortgage / contract / mgmt_pack /
 * bsa / enquiries / exchange / complete) is considered COMPLETE
 * when every required milestone scoped to it (backend
 * `milestones.items[].stage === node.id`) is done.  This
 * mirrors the rail's own P35 semantics — see
 * `DealRoomCanonicalPage.jsx::_stageBuckets` — so the Journey
 * rail, Current Stage panel, and Next Best Action resolver all
 * agree on which nodes have been ticked off.  Without this
 * check the hook fell back to `deal.completed_steps` alone,
 * which carries backend STAGE_ids (mortgage_app,
 * mortgage_offer, contract_pack, …) that never match the
 * canonical NODE ids the rail iterates over, so the first
 * uncompleted node was always the FIRST node — surfacing a
 * stale "Record Mortgage Application Submitted" NBA on deals
 * that were already past Contract. */
// Exchange / Completion only count once the lifecycle is confirmed (backend `_gate_terminal_steps` rule).
function _terminalGateOpen(nodeId, deal) {
  const st = String(deal?.stage || "").toLowerCase();
  const ss = String(deal?.status || "").toLowerCase();
  if (nodeId === "exchange") return ["exchanged", "completed"].includes(st) || ["exchanged", "completed"].includes(ss);
  if (nodeId === "complete" || nodeId === "completion") return st === "completed" || ss === "completed";
  return true;
}

function _isNodeCompleteByMilestones(nodeId, milestones, deal) {
  if (!nodeId || !Array.isArray(milestones) || milestones.length === 0) return false;
  if (!_terminalGateOpen(nodeId, deal)) return false;
  const scoped = milestones.filter((m) => m && m.stage === nodeId);
  if (scoped.length === 0) return false;
  const required = scoped.filter((m) => m.optional !== true);
  if (required.length === 0) return false;
  return required.every((m) => !!(m.completed || m.completed_at));
}


/* Derive per-node status from the `completedSteps` bag + the
 * deal's live `stage`. Returns nodes annotated with a status enum
 * of "completed" | "current" | "at_risk" | "pending".
 *
 * `at_risk` fires when a node is the current focus AND the deal
 * carries a `risk === "High"` marker, or when a blocker is
 * outstanding. Otherwise the first uncompleted node is the
 * current focus and the rest are pending. */
function _annotateNodes(nodes, deal, milestones, activeBlockers) {
  const completed = new Set();
  // iter-alignment · P0 · Journey SSOT Reconciliation (2026-02-15)
  // — accept both `completedSteps` (pilot camelCase) and
  // `completed_steps` (backend snake_case from milestone_completed
  // activity rows) so node status is derived from the same
  // canonical evidence the rail + Current Stage panel walk.
  const completedList = [
    ...(Array.isArray(deal?.completedSteps) ? deal.completedSteps : []),
    ...(Array.isArray(deal?.completed_steps) ? deal.completed_steps : []),
  ];
  completedList.forEach((id) => completed.add(id));
  // Also treat milestones with an actual completion timestamp as
  // done — the operator may have finished a step outside the
  // canonical activity log.
  for (const m of milestones || []) {
    const id = m?.step_id || m?.id;
    if (id && (m?.completed || m?.completed_at)) completed.add(id);
  }

  // iter-alignment · P0 · NBA Completed-Stage Guard (2026-08-16)
  // — Apply the SAME lifecycle-closure rule the rail uses so
  // baseline-inferred completions (e.g. deal at `contract_pack`
  // legitimately implies Mortgage is done) flip node status to
  // "completed".  Without this, `journeyNodes[mortgage].status`
  // stayed "current" on any deal that had advanced via
  // `deal.stage` alone — and the belt-and-braces NBA guard read
  // that status to decide whether to walk forward.  Result:
  // the guard was a permanent no-op.
  const _rawStage = String(deal?.stage || "").toLowerCase();
  const _baselineId = nodeIdForStage(_rawStage);
  const _baselineIdx = nodes.findIndex((n) => n && n.id === _baselineId);
  const _rawStatus = deal?.status || "";
  const _closedCompleted = closeCompletedSet({
    nodes,
    completedSet: completed,
    baselineIdx: _baselineIdx >= 0 ? _baselineIdx : null,
    exchangedFlag: _rawStage === "exchanged" || _rawStatus === "Exchanged",
    completedFlag: _rawStatus === "Completed",
  });

  // iter-alignment · P0 · NBA Completed-Stage Guard (2026-08-16)
  // — canonical node completion is determined by (a) the closed
  // completed set (rail-parity via lifecycle closure), OR (b) the
  // scoped-milestone helper.  This is the SAME rule the Journey
  // rail uses, so `activeStepId` never lags behind the visible
  // "Completed" chips on the rail.
  const isNodeComplete = (node) =>
    _closedCompleted.has(node.id) || _isNodeCompleteByMilestones(node.id, milestones, deal);

  const highRisk = String(deal?.risk || "").toLowerCase() === "high";
  const hasBlocker = _hasOutstandingBlocker(deal, milestones, activeBlockers);

  let currentIdx = -1;
  // Baseline stage is a floor (resolveCurrentFocusIdx rule) so the NBA never points behind the rail focus.
  for (let i = Math.max(0, _baselineIdx); i < nodes.length; i += 1) {
    if (!isNodeComplete(nodes[i])) { currentIdx = i; break; }
  }

  return nodes.map((node, idx) => {
    if (isNodeComplete(node)) {
      return { ...node, status: "completed" };
    }
    if (idx === currentIdx) {
      const atRisk = highRisk || hasBlocker;
      return { ...node, status: atRisk ? "at_risk" : "current" };
    }
    return { ...node, status: "pending" };
  });
}


/* iter-379 — Stale-blocker filter.
 *
 * A blocker is "stale" when the milestone / stage it belongs to has
 * already been marked complete on the deal. This happens routinely
 * on pilot deals where the seed data pins a static `blockers` array
 * (e.g. Mortgage Valuation Pending) that survives Exchange, and on
 * real deals when the backend blocker engine detects a lagging
 * document but the operator has already progressed past the stage
 * via a manual advance.
 *
 * Filtering is deterministic:
 *   1. explicit `status === "resolved"` ⇒ drop.
 *   2. blocker.step_id / journey_step / target_step in
 *      `deal.completedSteps` ⇒ drop.
 *   3. blocker.step_id maps to a canonical journey node whose stage
 *      list is STRICTLY EARLIER than the deal's current node ⇒ drop.
 *   4. blocker.stage / blocker.stage_id in DEAL_STAGES is STRICTLY
 *      EARLIER than the deal's current stage ⇒ drop.
 *
 * Applies to both the `deal.blockers` array AND the legacy
 * `deal.blockerState` string (mapped via `_BLOCKER_STATE_TO_NODE`).
 */

/* Ordered DEAL_STAGES ids — mirrors chainStore.DEAL_STAGES so the
 * hook stays dependency-free. Keep in sync manually if the pipeline
 * changes (it's stable since iter-258). */
const _DEAL_STAGES_ORDER = [
  "offer", "aml", "memo", "solicitors", "mortgage_app", "survey",
  "searches", "enquiries", "mortgage_offer", "exchange_ready",
  "exchanged", "completed",
];

/* Legacy `deal.blockerState` string → the journey node id whose
 * completion means the blocker is stale. Only strings that map to a
 * concrete stage appear here; open-ended labels like
 * "Chain Unconfirmed" stay unfiltered because they aren't tied to a
 * single milestone. */
const _BLOCKER_STATE_TO_NODE = {
  "Management Pack Outstanding":  "mgmt_pack",
  "Searches Delayed":             "contract",
  "Searches Underway":            "contract",
  "Mortgage Valuation Pending":   "mortgage",
  "Survey Queries Raised":        "mortgage",
  "Enquiries Raised":             "enquiries",
  "Contracts Issued":             "exchange",
  "Ready To Exchange":            "exchange",
  "Cash Buyer Progressing":       "mortgage",
};

function _blockerNodeId(b) {
  return b?.step_id || b?.journey_step || b?.target_step || b?.node_id || null;
}

function _nodeIndexOf(nodes, id) {
  if (!id) return -1;
  return nodes.findIndex((n) => n.id === id);
}

function _stageIndexOf(stage) {
  if (!stage) return -1;
  return _DEAL_STAGES_ORDER.indexOf(String(stage).toLowerCase());
}

export function filterActiveBlockers(deal, {
  applicableNodes = [],
  currentNodeId = null,
  /* P0 · Deal Room Data & Behaviour Integrity Pass (2026-08-12) —
   * Optional client-side sentinel of blocker ids that were just
   * resolved from the Critical Path card.  The backend re-fetch
   * happens on the next tick, but everything downstream (blocker
   * count, banner, health, Exchange Confidence, Next Best Action,
   * checklist strip) must reflect the resolution on the same
   * render pass so the operator never sees a contradictory
   * "resolved but still counted" state.  A `Set` of raw ids as
   * populated by `handleCriticalPathResolved`. */
  resolvedIds = null,
} = {}) {
  const raw = Array.isArray(deal?.blockers) ? deal.blockers : [];
  // iter-alignment · P0 · Journey SSOT Reconciliation (2026-02-15)
  // — accept BOTH camelCase (pilot pages) and snake_case (backend
  // `deal.completed_steps` derived from milestone_completed
  // activity rows) as authoritative evidence for "step done".
  // Blockers tied to a completed step are dropped regardless of
  // which source persisted the completion.
  const _completedCamel = Array.isArray(deal?.completedSteps) ? deal.completedSteps : [];
  const _completedSnake = Array.isArray(deal?.completed_steps) ? deal.completed_steps : [];
  const completed = new Set([..._completedCamel, ..._completedSnake]);
  const currentIdx = _nodeIndexOf(applicableNodes, currentNodeId);
  const currentStageIdx = _stageIndexOf(deal?.stage);
  // P0 · Correct Transaction Journey Completion Authority
  // (2026-08-18) — Lifecycle sentinel STRICT.  The moment the
  // deal is post-exchange (via ONE of the two explicit
  // authoritative signals — deal.stage set to `exchanged` /
  // `completed`, or `exchanged_at` / `completed_at` stamped),
  // pre-exchange blockers stop driving the transaction.
  // `completed_steps` inference is retired here for the same
  // reason it was retired on `deriveExchangeReadiness`: a stage
  // id enters that list only via child-milestone counts, and the
  // operator brief bans child-count-driven lifecycle inference.
  const _stage = String(deal?.stage || "").toLowerCase();
  const _stampSaysPost = !!deal?.exchanged_at || !!deal?.exchangedAt
    || !!deal?.completed_at || !!deal?.completedAt;
  const _stageSaysPost = _stage === "exchanged" || _stage === "completed";
  const isPostExchange = _stageSaysPost || _stampSaysPost;
  // Journey step ids that sit strictly before the `exchange` gate.
  // The list is intentionally hard-coded here (mirroring the
  // canonical journey node order in `transactionJourney.js`) so
  // this hook stays render-cycle cheap — every pre-exchange step
  // id is enumerated once and matched by set membership.
  const _PRE_EXCHANGE_STEP_IDS = new Set([
    "mortgage", "searches", "survey", "contract", "bsa",
    "mgmt_pack", "enquiries", "report",
  ]);
  // Deal-stage ids (chainStore `DEAL_STAGES`) that sit strictly
  // before the `exchanged` cursor.  Any blocker tagged with a
  // stage in this set is dropped when the deal is post-exchange.
  const _PRE_EXCHANGE_STAGE_IDS = new Set([
    "offer", "aml", "memo", "solicitors",
    "mortgage_app", "survey", "searches", "enquiries",
    "mortgage_offer", "exchange_ready",
  ]);
  const hasResolved = resolvedIds && typeof resolvedIds.has === "function";

  return raw.filter((b) => {
    if (!b) return false;
    if (String(b.status || "").toLowerCase() === "resolved") return false;
    // Optimistic-resolution sentinel — matches against the same
    // id shape (`id || step_id || title`) the Critical Path
    // resolver uses when it seeds the set.
    if (hasResolved) {
      const bid = String(b.id || b.step_id || b.title || "");
      if (bid && resolvedIds.has(bid)) return false;
    }
    const nodeId = _blockerNodeId(b);
    if (nodeId && completed.has(nodeId)) return false;
    if (nodeId) {
      const idx = _nodeIndexOf(applicableNodes, nodeId);
      // Node exists in the applicable rail AND is strictly earlier
      // than the current node ⇒ operator has already moved past it.
      if (idx >= 0 && currentIdx >= 0 && idx < currentIdx) return false;
    }
    const stageId = b.stage || b.stage_id || null;
    if (stageId) {
      const sIdx = _stageIndexOf(stageId);
      if (sIdx >= 0 && currentStageIdx >= 0 && sIdx < currentStageIdx) return false;
    }
    // iter-alignment · P0 · Journey SSOT Reconciliation
    // (2026-02-15) — lifecycle sentinel.  Once the deal is
    // post-exchange, any pre-exchange-scoped blocker is dropped
    // even if it doesn't carry an explicit step_id / stage tag
    // that would otherwise catch it.  A blocker only survives
    // the sentinel when it explicitly targets the post-exchange
    // `complete` step / `exchanged` / `completed` deal stage,
    // OR carries no lifecycle tag at all (treated as
    // lifecycle-agnostic).
    if (isPostExchange) {
      if (nodeId && _PRE_EXCHANGE_STEP_IDS.has(String(nodeId).toLowerCase())) return false;
      if (stageId && _PRE_EXCHANGE_STAGE_IDS.has(String(stageId).toLowerCase())) return false;
    }
    return true;
  });
}

/* Resolve whether the legacy `deal.blockerState` string is still
 * live. Returns the string when it hasn't been superseded, else
 * null. Used by `focusDetails.blockerReason` so completed stages
 * stop advertising stale copy. */
export function resolveLiveBlockerState(deal, { applicableNodes = [], currentNodeId = null } = {}) {
  const state = deal?.blockerState;
  if (!state || typeof state !== "string") return null;
  const trimmed = state.trim();
  if (!trimmed || trimmed.toLowerCase() === "no blocker logged") return null;
  if (!/risk|delay|outstanding|pending|raised|chain/i.test(trimmed)) return null;
  const mappedNodeId = _BLOCKER_STATE_TO_NODE[trimmed];
  if (!mappedNodeId) return trimmed;
  // iter-alignment · P0 · Journey SSOT Reconciliation (2026-02-15)
  // — honour both aliases when deciding whether the mapped node
  // is completed, so legacy blocker states stop advertising once
  // the backend `completed_steps` records the completion.
  const completed = new Set([
    ...(Array.isArray(deal?.completedSteps) ? deal.completedSteps : []),
    ...(Array.isArray(deal?.completed_steps) ? deal.completed_steps : []),
  ]);
  if (completed.has(mappedNodeId)) return null;
  const idx = _nodeIndexOf(applicableNodes, mappedNodeId);
  const currentIdx = _nodeIndexOf(applicableNodes, currentNodeId);
  if (idx >= 0 && currentIdx >= 0 && idx < currentIdx) return null;
  return trimmed;
}

function _hasOutstandingBlocker(deal, milestones, activeBlockers) {
  if (Array.isArray(activeBlockers) && activeBlockers.length > 0) return true;
  const dealBlocker = deal?.blocker || deal?.current_blocker;
  if (dealBlocker && String(dealBlocker).trim() && String(dealBlocker).trim().toLowerCase() !== "no blocker logged") {
    return true;
  }
  for (const m of milestones || []) {
    if (m?.blocker && String(m.blocker).trim()) return true;
  }
  return false;
}


/* Health picker. Terminal states short-circuit to "complete".
 * Otherwise: Blocker outstanding → "blocked", High risk → "at_risk",
 * Days-in-stage > 14 → "at_risk", else "healthy". Keeps the panel
 * copy aligned with `HEALTH_COPY`. */
function _resolveHealth(deal, milestones, isTerminal, activeBlockers) {
  if (isTerminal) return HEALTH_COPY.complete;
  if (_hasOutstandingBlocker(deal, milestones, activeBlockers)) return HEALTH_COPY.blocked;
  if (String(deal?.risk || "").toLowerCase() === "high") return HEALTH_COPY.at_risk;
  if (String(deal?.status || "") === "At Risk") return HEALTH_COPY.at_risk;
  const daysInStage = Number(deal?.days_in_stage || deal?.daysInStage || 0);
  if (daysInStage >= 14) return HEALTH_COPY.at_risk;
  return HEALTH_COPY.healthy;
}


/* Pure derivation. Extracted so it can be unit-tested without a
 * React render — the hook below just wraps this in `useMemo`. */
export function computeDealTransactionState(deal, { milestones = [], activities = [], participants = [], resolvedIds = null } = {}) {
    const safeDeal = deal || {};
    const rawStatus = safeDeal.status || "";
    const isTerminal = TERMINAL_STATUSES.has(rawStatus);
    const terminalLabel = isTerminal ? (TERMINAL_LABEL[rawStatus] || rawStatus) : null;

    // Terminal date — prefer the most-relevant field, fall back
    // to the deal's own updated_at so the banner is never blank.
    const terminalIso = (() => {
      if (!isTerminal) return null;
      if (rawStatus === "Exchanged")  return safeDeal.exchanged_at || safeDeal.target_exchange || safeDeal.updated_at;
      if (rawStatus === "Completed")  return safeDeal.completed_at || safeDeal.target_completion || safeDeal.updated_at;
      return safeDeal.fell_through_at || safeDeal.updated_at || null;
    })();

    const applicableNodes = _buildApplicableNodes(safeDeal);
    /* iter-379 — Compute the current node id BEFORE we filter
     * blockers so the filter can drop anything tied to an earlier
     * stage. `_currentNodeId` is derived without a health check
     * (which itself depends on active blockers) to break the cycle. */
    // iter-alignment · P0 · Journey SSOT Reconciliation (2026-02-15)
    // — the backend now populates `deal.completed_steps` (snake_case)
    // from `milestone_completed` activity rows.  Both aliases fold
    // into the same `_completedSet` so `_currentNodeId` reconciles
    // with the Deal Room's Current Stage panel even before the
    // milestones endpoint fetch resolves.
    const _completedSet = new Set([
      ...(Array.isArray(safeDeal?.completedSteps) ? safeDeal.completedSteps : []),
      ...(Array.isArray(safeDeal?.completed_steps) ? safeDeal.completed_steps : []),
    ]);
    for (const m of milestones || []) {
      const mid = m?.step_id || m?.id;
      if (mid && (m?.completed || m?.completed_at)) _completedSet.add(mid);
    }
    let _currentNodeId = null;
    // iter-alignment · P0 · NBA Completed-Stage Guard (2026-08-16)
    // — Apply the SAME lifecycle-closure rule the rail uses
    // (`closeCompletedSet`) so nodes that are inferred complete
    // by the deal's baseline `stage` (e.g. a deal at `contract_pack`
    // legitimately implies Mortgage is done) advance the current
    // focus even when the backend `completed_steps` rollup carries
    // stage-alias ids (mortgage_app, mortgage_offer, …) that never
    // match canonical NODE ids.  Without this, `activeStepId`
    // permanently pointed at `mortgage` on any deal that had moved
    // on via `deal.stage` alone, which drove the NBA to surface
    // "Record Mortgage Application Submitted" on already-past-
    // mortgage deals.  Reuses existing SSOT — no new state.
    const _baselineId = nodeIdForStage(String(safeDeal.stage || "").toLowerCase());
    const _baselineIdx = applicableNodes.findIndex((n) => n && n.id === _baselineId);
    const _closedCompletedSet = closeCompletedSet({
      nodes: applicableNodes,
      completedSet: _completedSet,
      baselineIdx: _baselineIdx >= 0 ? _baselineIdx : null,
      exchangedFlag: String(safeDeal.stage || "").toLowerCase() === "exchanged"
        || rawStatus === "Exchanged",
      completedFlag: rawStatus === "Completed",
    });
    for (const n of applicableNodes.slice(Math.max(0, _baselineIdx))) {
      // A node is complete when EITHER (a) the closed completed
      // set (rail-parity) contains its id, OR (b) every required
      // milestone scoped to it is done.  Same rule as
      // `_annotateNodes` — surfaces stay in lockstep.
      if (!_closedCompletedSet.has(n.id) && !_isNodeCompleteByMilestones(n.id, milestones, safeDeal)) {
        _currentNodeId = n.id;
        break;
      }
    }
    const activeBlockers = filterActiveBlockers(safeDeal, {
      applicableNodes,
      currentNodeId: _currentNodeId,
      resolvedIds,
    });
    const activeBlockerState = resolveLiveBlockerState(safeDeal, {
      applicableNodes,
      currentNodeId: _currentNodeId,
    });

    const annotatedNodes = _annotateNodes(applicableNodes, safeDeal, milestones, activeBlockers);

    const currentNode = isTerminal
      ? null
      : annotatedNodes.find((n) => n.status === "current" || n.status === "at_risk") || null;

    /* iter-372 — Pre-journey resolution.
     *
     * When the deal is in a pre-journey stage (offer / memo / aml)
     * the CANONICAL rail (Mortgage → …) hasn't started yet. The
     * operator's next action lives in the pre-journey (Memorandum of
     * Sale, AML checks, etc.). We expose the active pre-journey step
     * so every surface — Journey rail focus, NBA card, Milestone
     * Progression Sheet — reads the same "current step" regardless of
     * whether the deal is pre-journey or on-rail. This unifies the
     * previously conflicting sources (backend `journeyState.current_step`
     * vs the frontend canonical rail focus). */
    const rawStage = String(safeDeal.stage || "").toLowerCase();
    const _isPreJourney = !isTerminal && isPreJourney(rawStage);
    const preJourneyNode = _isPreJourney
      ? (PRE_JOURNEY.find((p) => (p.stages || []).includes(rawStage)) || PRE_JOURNEY[0])
      : null;

    /* activeStepId — SINGLE source of truth for "the step the operator
     * is currently working on". Pre-journey stages return the pre-journey
     * node id ("offer" / "memo" / "aml"). On-rail deals return the
     * canonical rail node id. Terminal deals return null. */
    const activeStepId = isTerminal
      ? null
      : (preJourneyNode?.id || currentNode?.id || null);
    const activeStepLabel = isTerminal
      ? null
      : (preJourneyNode?.longLabel || preJourneyNode?.label
         || currentNode?.longLabel || currentNode?.label || null);

    const health = _resolveHealth(safeDeal, milestones, isTerminal, activeBlockers);

    const flags = resolveDealFlags(safeDeal);

    // Waiting chip. Terminal states + no current node ⇒ suppress.
    const waitingIso =
      safeDeal.stage_entered_at ||
      safeDeal.updated_at ||
      null;
    const waitingDays = isTerminal ? null : daysSince(waitingIso);
    const waitingLabel = isTerminal ? null : waitingChip(waitingDays);

    // Next Best Action label. Terminal states clear it; otherwise
    // the current node's own copy drives it (pre-journey nodes have
    // their own copy so agents see "Memorandum of Sale Issued" etc.).
    const nextBestAction = isTerminal
      ? null
      : (preJourneyNode?.longLabel || preJourneyNode?.label
         || currentNode?.nextActionLabel || currentNode?.label || null);

    // iter-365 — Dynamic Exchange Confidence. Reuses the applicable
    // journey nodes we already resolved above so the transaction-type
    // skip rules (cash-buyer / chain-free / freehold) apply here too.
    // P0 · 2026-08-12 — `resolvedIds` threaded so the confidence
    // score bumps up on the same render as the count drops.
    const confidence = computeExchangeConfidence(safeDeal, {
      milestones,
      activities,
      participants,
      applicableNodes: applicableNodes,
      flags,
      resolvedIds,
    });

    return {
      isTerminal,
      terminalKind: isTerminal ? rawStatus : null,
      terminalLabel,
      terminalDateIso: terminalIso,
      /* P0 · Property Type + Readiness Lifecycle (2026-02-28)
       * Surface the canonical target completion ISO so the
       * post-exchange terminal banner can render
       * "Completion scheduled for {date}" without a second
       * lookup.  Reads the same field the hero + journey use. */
      completionTargetIso:
        safeDeal?.target_completion ||
        safeDeal?.targetCompletion ||
        safeDeal?.completion_date ||
        safeDeal?.completionDate ||
        null,
      journeyNodes: annotatedNodes,
      currentNode,
      currentFocusLabel: currentNode?.label || null,
      // iter-372 — unified active step across pre-journey + rail.
      isPreJourney: _isPreJourney,
      preJourneyNode,
      activeStepId,
      activeStepLabel,
      health,
      hasBlocker: _hasOutstandingBlocker(safeDeal, milestones, activeBlockers),
      /* iter-379 — Filtered, stage-aware view of the deal's blockers.
       * Consumers MUST prefer these over `deal.blockers` /
       * `deal.blockerState` so completed stages stop advertising
       * stale copy. */
      activeBlockers,
      activeBlockerState,
      activeBlockerCount: activeBlockers.length,
      waitingDays,
      waitingLabel,
      waitingIso,
      nextBestAction,
      flags,
      confidence,
    };
}


/* React binding. */
export function useDealTransactionState(deal, opts = {}) {
  return useMemo(
    () => computeDealTransactionState(deal, opts),
    // opts is a plain object literal at the callsite, so keying
    // on its three arrays is stable enough for our use.
    // P0 · Deal Room Data & Behaviour Integrity Pass (2026-08-12) —
    // `resolvedIds` (client sentinel Set) is now part of the memo
    // key so the moment a Critical Path is resolved, every
    // downstream derivation (health, count, banner, NBA, Exchange
    // Confidence) recomputes on the same render pass.
    [deal, opts.milestones, opts.activities, opts.participants, opts.resolvedIds],
  );
}
